import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../../components/Button/Button';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import './ConfirmAccount.css';

function ConfirmAccount({ onLogin }) {
  const [confirmationCode, setConfirmationCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill email and password from previous state
  useEffect(() => {
    if (location.state) {
      setEmail(location.state.email || '');
      setPassword(location.state.password || '');
    }
  }, [location]);

  const handleConfirm = async () => {
    if (!confirmationCode || !email || !password) {
      setMessage("Please fill in all fields.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/confirm`, {
        email,
        confirmationCode,
        password,
      });

      // Check the correct structure of response.data
      if (response.data && response.data.idToken && response.data.accessToken && response.data.refreshToken) {
        const { idToken, accessToken, refreshToken } = response.data;
        setMessage("Account confirmed and logged in successfully!");
        setMessageType("success");

        // Call onLogin with all tokens
        onLogin(idToken, accessToken, refreshToken);

        // Redirect to the admin dashboard or home page
        navigate("/dashboard");
      } else {
        setMessage("No valid tokens received from the backend.");
        setMessageType("error");
      }
    } catch (error) {
      setMessage(error.response?.data?.error || error.response?.data?.message || "Account confirmation failed.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      setMessage("Please enter your email to resend confirmation.");
      setMessageType("error");
      return;
    }

    setResendLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/resend-confirmation`, {
        email,
      });
      setMessage("Confirmation email resent. Please check your inbox and spam folder.");
      setMessageType("success");
    } catch (error) {
      setMessage(error.response?.data?.error || error.response?.data?.message || "Failed to resend confirmation.");
      setMessageType("error");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="confirm-container">
      {(loading || resendLoading) && (
        <LoadingOverlay label={loading ? 'Confirming account' : 'Resending confirmation email'} />
      )}
      <div className="confirm-box">
        <h2>Confirm Your Account</h2>
        <p className="message success">
          A confirmation code was sent to your email. Please check your inbox and spam folder.
        </p>
        <input
          type="text"
          placeholder="Enter confirmation code"
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)}
          className="input-field"
        />
        <Button
          text={loading ? "..." : "Confirm"}
          onClick={handleConfirm}
          styleType="default"
          className="confirm-button"
          disabled={loading || resendLoading}
        />
        <p className={`message ${messageType}`}>{message}</p>
        {/* Resend Confirmation Email */}
        <div className="message-container">
          <p>Didn't receive the email?</p>
          <button
            type="button"
            className="message-container-link"
            onClick={() => {
              if (loading || resendLoading) return;
              handleResendConfirmation();
            }}
            disabled={loading || resendLoading}
          >
            {resendLoading ? "Resending..." : "Resend Confirmation Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmAccount;
